import { supabase } from '../lib/supabase';
import type {
  Client,
  ClientCreditsStatus,
  CreditTransaction,
  PlanTier,
  Production,
} from '../types/db';

function ensureClient() {
  if (!supabase) throw new Error('Supabase client not initialized — check env vars');
  return supabase;
}

// ── Admin Queries ─────────────────────────────────────────

export async function listAllClients(): Promise<ClientCreditsStatus[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('v_client_credits_status')
    .select('*')
    .order('name', { ascending: true });
  if (error) throw error;
  return (data as ClientCreditsStatus[]) ?? [];
}

export async function getClientFull(id: string): Promise<Client | null> {
  const sb = ensureClient();
  const { data, error } = await sb.from('clients').select('*').eq('id', id).single();
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return (data as Client) ?? null;
}

export async function listClientTransactions(
  clientId: string,
  limit = 10,
): Promise<CreditTransaction[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('credit_transactions')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data as CreditTransaction[]) ?? [];
}

export async function listClientProductions(clientId: string): Promise<Production[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('productions')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as Production[]) ?? [];
}

// ── Admin Mutations (Edge Functions) ──────────────────────

export async function adminConfirmPayment(
  clientId: string,
  notes?: string,
): Promise<{ success: boolean }> {
  const sb = ensureClient();
  const { data, error } = await sb.functions.invoke('scriptto-admin-confirm-payment', {
    body: { client_id: clientId, notes },
  });
  if (error) throw new Error(error.message);
  return (data as { success: boolean }) ?? { success: false };
}

export async function adminAddCredits(
  clientId: string,
  credits: number,
  notes?: string,
): Promise<{ success: boolean }> {
  const sb = ensureClient();
  const { data, error } = await sb.functions.invoke('scriptto-admin-add-credits', {
    body: { client_id: clientId, credits, notes },
  });
  if (error) throw new Error(error.message);
  return (data as { success: boolean }) ?? { success: false };
}

export async function adminChangePlan(
  clientId: string,
  newTier: PlanTier,
): Promise<{ success: boolean }> {
  const sb = ensureClient();
  const { data, error } = await sb.functions.invoke('scriptto-admin-change-plan', {
    body: { client_id: clientId, new_tier: newTier },
  });
  if (error) throw new Error(error.message);
  return (data as { success: boolean }) ?? { success: false };
}

export async function adminUpdateCoefficient(
  productionId: string,
  coefficient: number,
): Promise<{ success: boolean }> {
  const sb = ensureClient();
  const { data, error } = await sb.functions.invoke('scriptto-admin-update-coefficient', {
    body: { production_id: productionId, coefficient },
  });
  if (error) throw new Error(error.message);
  return (data as { success: boolean }) ?? { success: false };
}
